import React, { useState, useEffect, memo } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Sidebar = memo(({ viewMode = 'detailed', showTabBar = false }) => {
  const location = useLocation();
  const { sidebarExpanded, toggleSidebar } = useSidebar();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // Changed from 768 to 1024 for better tablet support
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if we're in Simple view based on prop
  const isSimpleView = viewMode === 'simple';

  // Navigation items for Simple view (minimal)
  const simpleNavigationItems = [
    {
      label: 'Dashboard',
      path: '/simple',
      icon: 'Zap',
      description: 'Quick retirement check'
    }
  ];

  // Navigation items for Detailed view (full)
  const detailedNavigationItems = [
    {
      label: 'Dashboard',
      path: '/wealth-dashboard',
      icon: 'LayoutDashboard',
      description: 'Retirement readiness overview'
    },
    {
      label: 'Monte Carlo 🚀',
      path: '/monte-carlo',
      icon: 'BarChart3',
      description: 'Simulated scenario analysis'
    },
    {
      label: 'Learning Center',
      path: '/learning-center',
      icon: 'BookOpen',
      description: 'Financial education and resources'
    },
    {
      label: 'Planning',
      icon: 'Calculator',
      children: [
        {
          label: 'Asset Portfolio',
          path: '/asset-portfolio',
          icon: 'PieChart',
          description: 'Investment and asset allocation'
        },
        {
          label: 'Income Management',
          path: '/income-management',
          icon: 'TrendingUp',
          description: 'Salary and income projections'
        },
        {
          label: 'Investments & Allocations',
          path: '/investments-allocations',
          icon: 'Wallet',
          description: 'Recurring investments and asset allocation'
        },
        {
          label: 'Expense Tracking',
          path: '/expense-tracking',
          icon: 'Receipt',
          description: 'Budget and spending analysis'
        },
        {
          label: 'Debt & Liabilities',
          path: '/debt-liabilities',
          icon: 'CreditCard',
          description: 'Loan tracking and debt management'
        },
        {
          label: 'Setup',
          path: '/setup',
          icon: 'Settings',
          description: 'Basic retirement parameters'
        }
      ]
    },
    /* Analysis section - temporarily disabled
    {
      label: 'Analysis',
      path: '/scenario-planning',
      icon: 'GitBranch',
      description: 'Scenario modeling and stress testing'
    }
    */
    // Reports menu temporarily hidden
    // {
    //   label: 'Reports',
    //   path: '/reports-analytics',
    //   icon: 'FileText',
    //   description: 'Documentation and analytics'
    // }
  ];

  // Use appropriate navigation items based on current view
  const navigationItems = isSimpleView ? simpleNavigationItems : detailedNavigationItems;

  const isActiveItem = (path) => {
    return location.pathname === path;
  };

  const hasActiveChild = (children) => {
    return children?.some(child => isActiveItem(child.path));
  };

  const renderNavigationItem = (item, isChild = false) => {
    const isActive = isActiveItem(item.path);
    const hasActiveChildren = hasActiveChild(item.children);
    
    const baseClasses = `
      flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium
      transition-all duration-200 ease-spring group relative
      ${isChild ? 'ml-6 pl-6' : ''}
    `;
    
    const activeClasses = isActive 
      ? 'bg-primary text-primary-foreground shadow-soft' 
      : hasActiveChildren
      ? 'bg-muted text-foreground'
      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50';

    if (item.children) {
      return (
        <div key={item.label} className="space-y-1">
          <div className={`${baseClasses} ${hasActiveChildren ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}>
            <Icon name={item.icon} size={20} className="shrink-0" />
            {sidebarExpanded && (
              <span className="truncate">{item.label}</span>
            )}
          </div>
          {sidebarExpanded && (
            <div className="space-y-1">
              {item.children.map(child => renderNavigationItem(child, true))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`${baseClasses} ${activeClasses}`}
        title={!sidebarExpanded ? item.label : ''}
      >
        <Icon name={item.icon} size={20} className="shrink-0" />
        {sidebarExpanded && (
          <span className="truncate">{item.label}</span>
        )}
        {isActive && (
          <div className="absolute right-2 w-2 h-2 bg-primary-foreground rounded-full opacity-75" />
        )}
      </Link>
    );
  };

  if (isMobile) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-1100">
        <div className="flex items-center justify-around px-1 py-1">
          {navigationItems.map(item => {
            if (item.children) {
              return item.children.map(child => (
                <Link
                  key={child.path}
                  to={child.path}
                  className={`
                    flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-xs font-medium
                    transition-all duration-200 ease-spring min-w-0 flex-1
                    ${isActiveItem(child.path) 
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={child.icon} size={18} />
                  <span className="truncate max-w-full text-xs">{child.label.split(' ')[0]}</span>
                </Link>
              ));
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-xs font-medium
                  transition-all duration-200 ease-spring min-w-0 flex-1
                  ${isActiveItem(item.path) 
                    ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <Icon name={item.icon} size={18} />
                <span className="truncate max-w-full text-xs">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <aside 
        className={`
          fixed top-0 h-full bg-card border-r border-border z-1000
          transition-all duration-300 ease-spring
          ${sidebarExpanded ? 'w-64' : 'w-16'}
          lg:translate-x-0 ${showTabBar ? 'lg:left-12' : 'lg:left-0'} left-0
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="flex items-center gap-3 p-6 border-b border-border">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg shrink-0 text-primary-foreground text-lg font-bold">₹</div>
            {sidebarExpanded && (
              <div className="flex flex-col">
                <h1 className="text-lg font-semibold text-foreground">RetireProApp</h1>
                <p className="text-xs text-muted-foreground">Financial Planning</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigationItems.map(item => renderNavigationItem(item))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <div className="px-3 py-2 text-xs text-muted-foreground space-y-2">
              <div className="flex items-center gap-3">
                <Icon name="Shield" size={16} className="shrink-0" />
                {sidebarExpanded && (
                  <span className="truncate">Secure & Encrypted</span>
                )}
              </div>
              {sidebarExpanded && (
                <a
                  href="mailto:support@retireproapp.com"
                  className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  <Icon name="Mail" size={14} />
                  <span className="truncate">support@retireproapp.com</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Toggle Button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 z-1200 lg:hidden bg-card border border-border rounded-lg p-2 shadow-soft"
        aria-label="Toggle navigation"
      >
        <Icon name="Menu" size={20} />
      </button>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if viewMode changes
  return prevProps.viewMode === nextProps.viewMode;
});

export default Sidebar;